import { applicationErrorMessage } from "../lib/applicationErrors.mjs";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

export default function SavedJobs() {
  const [saved, setSaved] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [form, setForm] = useState({ name: "", keywords: "", country: "", sponsorship_required: false, frequency: "manual" });
  const [message, setMessage] = useState("");

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [actionError, setActionError] = useState("");
  const [pending, setPending] = useState("");
  const actionPending = useRef(false);
  const loadPending = useRef(false);

  const load = useCallback(async () => {
    if (loadPending.current) return;
    loadPending.current = true;
    setLoading(true);
    setLoadError("");
    try {
      const [savedResponse, alertsResponse] = await Promise.all([api.get("/jobs/saved"), api.get("/job-alerts")]);
      setSaved(savedResponse.data.items || []);
      setAlerts(alertsResponse.data.items || []);
    } catch (error) {
      setLoadError(error?.response?.status === 401
        ? "Please sign in to manage saved jobs and alerts."
        : applicationErrorMessage(error, "Unable to load saved jobs and alerts. Please try again."));
    } finally {
      loadPending.current = false;
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const createAlert = async (event) => {
    event.preventDefault();
    if (actionPending.current) return;
    actionPending.current = true;
    setPending("create");
    setMessage("");
    setActionError("");
    try {
      const { data } = await api.post("/job-alerts", {
        ...form,
        keywords: form.keywords.split(",").map((value) => value.trim()).filter(Boolean),
      });
      setAlerts((items) => [data, ...items]);
      setForm({ name: "", keywords: "", country: "", sponsorship_required: false, frequency: "manual" });
      setMessage("Alert saved.");
    } catch (error) {
      setActionError(applicationErrorMessage(error, "Unable to save this alert. Please try again."));
    } finally {
      actionPending.current = false;
      setPending("");
    }
  };

  const removeItem = async (type, id) => {
    if (actionPending.current) return;
    actionPending.current = true;
    setPending(type + ":" + id);
    setMessage("");
    setActionError("");
    try {
      await api.delete(type === "alert" ? "/job-alerts/" + id : "/jobs/" + id + "/save");
      if (type === "alert") setAlerts((items) => items.filter((item) => item.id !== id));
      else setSaved((items) => items.filter((item) => item.job_id !== id));
      setMessage(type === "alert" ? "Alert removed." : "Vacancy removed from saved jobs.");
    } catch (error) {
      setActionError(applicationErrorMessage(error, "Unable to remove this item. Please try again."));
    } finally {
      actionPending.current = false;
      setPending("");
    }
  };

  if (loading || loadError) return (
    <div className="max-w-7xl mx-auto px-5 py-10" data-testid="saved-jobs-page">
      <h1 className="text-3xl font-semibold">Saved jobs and alerts</h1>
      {loading ? <p role="status" className="mt-4 text-sm text-slate-600">Loading saved jobs and alerts…</p> : <>
        <p role="alert" className="mt-4 text-sm text-red-700">{loadError}</p>
        <button className="btn btn-primary mt-4" onClick={load}>Retry loading</button>
      </>}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-5 py-10" data-testid="saved-jobs-page">
      <p className="overline">Your opportunity workspace</p>
      <h1 className="mt-3 text-3xl font-semibold">Saved jobs and alerts</h1>
      <p className="mt-3 max-w-2xl text-sm text-slate-600">
        Save vacancies for later and keep an auditable search preference. Alerts are currently a staff follow-up queue;
        automatic email or WhatsApp delivery can be added after an official channel is configured.
      </p>

      {message && <p role="status" className="mt-4 text-sm text-emerald-700" data-testid="saved-jobs-message">{message}</p>}
      {actionError && <p role="alert" className="mt-4 text-sm text-red-700">{actionError}</p>}

      <div className="mt-8 grid gap-6 lg:grid-cols-[1.35fr_1fr]">
        <section className="card p-6">
          <h2 className="text-lg font-semibold">Saved vacancies ({saved.length})</h2>
          <div className="mt-4 space-y-3">
            {saved.map((item) => (
              <div key={item.id} className="rounded-xl border border-slate-200 p-4" data-testid={"saved-job-" + item.job_id}>
              <Link to={"/jobs/" + item.job_id} className="block hover:underline">
                <p className="font-semibold">{item.job_title}</p>
                <p className="text-sm text-slate-600">{item.company_name} · {item.town || item.country || "Location not specified"}</p>
              </Link>
              <button className="btn btn-ghost mt-3" disabled={!!pending} onClick={() => removeItem("vacancy", item.job_id)}>
                {pending === "vacancy:" + item.job_id ? "Removing…" : "Remove saved vacancy"}
              </button>
              </div>
            ))}
            {saved.length === 0 && <p className="text-sm text-slate-500">No saved vacancies yet. <Link className="underline" to="/jobs">Browse jobs</Link>.</p>}
          </div>
        </section>

        <section className="card p-6">
          <h2 className="text-lg font-semibold">Create a job alert</h2>
          <form className="mt-4" onSubmit={createAlert}>
            <fieldset disabled={!!pending} className="space-y-3">
            <input className="field" required placeholder="Alert name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input className="field" placeholder="Keywords, separated by commas" value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} />
            <input className="field" placeholder="Country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
            <select className="field" value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })}>
              <option value="manual">Manual review queue</option>
              <option value="daily">Daily review queue</option>
              <option value="weekly">Weekly review queue</option>
            </select>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.sponsorship_required} onChange={(e) => setForm({ ...form, sponsorship_required: e.target.checked })} />
              Sponsorship evidence required
            </label>
            <button className="btn btn-primary w-full justify-center" data-testid="create-job-alert">{pending === "create" ? "Saving alert…" : "Save alert"}</button>
            </fieldset>
          </form>
        </section>
      </div>

      <section className="card p-6 mt-6">
        <h2 className="text-lg font-semibold">Your alerts ({alerts.length})</h2>
        <ul className="mt-4 space-y-2 text-sm">
          {alerts.map((alert) => (
            <li key={alert.id} className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-2">
              <span><strong>{alert.name}</strong> · {alert.country || "All countries"} · {alert.frequency}</span>
              <button className="btn btn-ghost py-1 px-3" disabled={!!pending} onClick={() => removeItem("alert", alert.id)}>{pending === "alert:" + alert.id ? "Removing…" : "Remove"}</button>
            </li>
          ))}
          {alerts.length === 0 && <li className="text-slate-500">No alerts configured.</li>}
        </ul>
      </section>
    </div>
  );
}
